import { useSSRContext } from 'vue';
import type { BasicContext } from './types';

declare global {
  interface Window {
    aemNamespace: {
      componentData: Record<string, Record<string, unknown>>;
    };
    __GLOBAL_CONTEXT__?: {
      get: () => {
        componentData: Record<string, Record<string, unknown>>;
      };
    };
  }
}

/**
 * Get the component data from the AEM context
 * @template ComponentData The type of the component data (Just a type to help with the typescript typing)
 * @param componentIdSelector The component ID or an array of component IDs to get the data for. If not provided, it will return the entire component data
 * @returns
 */
function getComponentData<ComponentData = Record<string, unknown>>(componentId: string): BasicContext<ComponentData>;
function getComponentData<ComponentData = Record<string, unknown>>(
  componentIdSelector?: string | string[] | undefined,
): Record<string, BasicContext<ComponentData>>;
function getComponentData<ComponentData = Record<string, unknown>>(
  componentIdSelector?: string | string[] | undefined,
): BasicContext<ComponentData> | Record<string, BasicContext<ComponentData>> {
  const isBrowser = typeof window !== 'undefined';
  if (!isBrowser) {
    const AEMContext = useSSRContext()?.AEMContext;

    return {
      payload: AEMContext?.payload,
      config: AEMContext?.config,
    };
  }
  const topMostWindow = window.top || window;
  const componentData =
    topMostWindow.__GLOBAL_CONTEXT__?.get()?.componentData ?? topMostWindow.aemNamespace?.componentData;

  if (!componentIdSelector) {
    return componentData as Record<string, BasicContext<ComponentData>>;
  }

  if (Array.isArray(componentIdSelector)) {
    return Object.fromEntries(componentIdSelector.map((id) => [id, componentData[id] as BasicContext<ComponentData>]));
  }

  return componentData[componentIdSelector] as BasicContext<ComponentData>;
}

export { getComponentData, BasicContext };
